import { de, enGB, es, et, fi, fr, nb, pl, ru, sv } from 'date-fns/locale';
import localeEnglish from './languages/en-GB.json';
import getMessagesByLanguage from './languages';

const supportedLocales = [
  'de-DE',
  'en-GB',
  'es-ES',
  'et-EE',
  'fi-FI',
  'fr-FR',
  'nb-NO',
  'pl-PL',
  'ru-RU',
  'sv-SE'
] as const;

const dateFnsLocales = {
  'de-DE': de,
  'en-GB': enGB,
  'es-ES': es,
  'et-EE': et,
  'fi-FI': fi,
  'fr-FR': fr,
  'nb-NO': nb,
  'pl-PL': pl,
  'ru-RU': ru,
  'sv-SE': sv
};

const localeNames = {
  'de-DE': 'Deutsch',
  'en-GB': 'English',
  'es-ES': 'Español',
  'et-EE': 'Eesti',
  'fi-FI': 'Suomi',
  'fr-FR': 'Français',
  'nb-NO': 'Norsk',
  'pl-PL': 'Polski',
  'ru-RU': 'Русский',
  'sv-SE': 'Svenska'
};

const localeAlternatives = {
  de: 'de-DE',
  en: 'en-GB',
  es: 'es-ES',
  et: 'et-EE',
  fi: 'fi-FI',
  fr: 'fr-FR',
  nb: 'nb-NO',
  pl: 'pl-PL',
  ru: 'ru-RU',
  sv: 'sv-SE'
};

// Short language codes shared by course languages and flag icons.
// Note that Norwegian is 'no' here, as it is 'no' in Wikipedia language codes,
// and not 'nb' as you might expect since the interface locale is 'nb-NO'.
const localeLanguageCodes = {
  'de-DE': 'de',
  'en-GB': 'en',
  'es-ES': 'es',
  'et-EE': 'et',
  'fi-FI': 'fi',
  'fr-FR': 'fr',
  'nb-NO': 'no',
  'pl-PL': 'pl',
  'ru-RU': 'ru',
  'sv-SE': 'sv'
};

const getLanguage = ({
  cookies,
  defaultSystemLanguage
}: {
  cookies: any;
  defaultSystemLanguage: string;
}): Locales => {
  let { language } = cookies;

  if (!language || !supportedLocales.includes(language)) {
    language = defaultSystemLanguage;
  }

  return language;
};

const getMessageWithPlaceholders = ({
  message,
  placeholders
}: {
  message: string;
  placeholders: {
    [key: string]: string | number;
  };
}) => {
  let newMessage = message;
  Object.keys(placeholders).forEach((key) => {
    const regex = new RegExp(`{{\\s?${key}\\s?}}`);
    newMessage = newMessage.replace(regex, () => String(placeholders[key]));
  });

  return newMessage;
};

type Locales = (typeof supportedLocales)[number];
type LocaleMessages = ReturnType<typeof getMessagesByLanguage>;
export type { Locales, LocaleMessages };

export {
  getLanguage,
  getMessagesByLanguage,
  getMessageWithPlaceholders,
  dateFnsLocales,
  localeAlternatives,
  localeEnglish,
  localeLanguageCodes,
  localeNames,
  supportedLocales
};
